import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { motion } from "framer-motion";
import { Handshake, Crown, Calendar, ScrollText, Loader2, Plus, X, ChevronRight, Users2 } from "lucide-react";
import { toast } from "sonner";

const STATUS_COLOR = {
  planned: "#94A3B8",
  confirmed: "#22C55E",
  held: "#D4AF37",
  cancelled: "#EF4444",
};

export default function Diplomacy() {
  const [engagements, setEngagements] = useState([]);
  const [protocols, setProtocols] = useState([]);
  const [stakeholders, setStakeholders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [activeEng, setActiveEng] = useState(null);
  const [influencePath, setInfluencePath] = useState(null);
  const [pathLoading, setPathLoading] = useState(false);

  const blank = { title: "", protocol_type: "ministerial", objective: "", scheduled_date: "", target_stakeholder_id: "", target_stakeholder_name: "", status: "planned" };
  const [newEng, setNewEng] = useState(blank);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/diplomacy/engagements"),
      api.get("/diplomacy/protocols"),
      api.get("/stakeholders"),
    ]).then(([e, p, s]) => {
      setEngagements(e.data);
      setProtocols(p.data);
      setStakeholders(s.data);
    }).finally(() => setLoading(false));
  };

  useEffect(load, []);

  const save = async () => {
    if (!newEng.title.trim()) return;
    try {
      const sh = stakeholders.find((s) => s.id === newEng.target_stakeholder_id);
      const payload = { ...newEng, target_stakeholder_name: sh?.name || newEng.target_stakeholder_name };
      await api.post("/diplomacy/engagements", payload);
      setNewEng(blank);
      setCreating(false);
      load();
      toast.success("Engagement scheduled");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const remove = async (eid) => {
    try {
      await api.delete(`/diplomacy/engagements/${eid}`);
      setActiveEng(null);
      load();
    } catch { toast.error("Delete failed"); }
  };

  const findPath = async (sid) => {
    setPathLoading(true);
    setInfluencePath(null);
    try {
      const r = await api.get(`/diplomacy/influence-path/${sid}`);
      setInfluencePath(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not compute pathway");
    } finally {
      setPathLoading(false);
    }
  };

  const selectedProtocol = protocols.find((p) => p.id === (activeEng?.protocol_type || newEng.protocol_type));

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="diplomacy-page">
      <div>
        <div className="label-eyebrow mb-1">// Strategic Influence & Diplomacy</div>
        <h1 className="font-display text-3xl text-white tracking-tight">Diplomacy Layer</h1>
        <p className="text-slate-400 text-sm mt-1 max-w-2xl">
          High-level engagement coordination — protocol, talking points, and influence-pathway mapping for the
          institutional moments that move outcomes.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Engagements list */}
        <div className="lg:col-span-1 nexus-glass-panel p-5" data-testid="engagement-list">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Handshake className="w-3.5 h-3.5" style={{ color: "var(--gold-400)" }} strokeWidth={1.6} />
              <span className="label-eyebrow">// Engagements</span>
            </div>
            <button onClick={() => { setCreating(!creating); setActiveEng(null); }} data-testid="new-engagement-btn" className="text-[10px] font-mono tracking-widest px-2 py-1 border border-white/10 hover:border-white/30 flex items-center gap-1">
              <Plus className="w-3 h-3" /> NEW
            </button>
          </div>
          {loading ? (
            <div className="py-10 text-slate-400 font-mono text-xs flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" /> LOADING…</div>
          ) : engagements.length === 0 && !creating ? (
            <div className="py-6 text-center text-[12px] text-slate-500">No engagements scheduled.</div>
          ) : (
            <div className="space-y-1.5 max-h-[600px] overflow-y-auto pr-1">
              {engagements.map((e) => (
                <button
                  key={e.id}
                  onClick={() => { setActiveEng(e); setCreating(false); }}
                  data-testid={`engagement-${e.id}`}
                  className={`w-full text-left p-2.5 border transition-all ${activeEng?.id === e.id ? "border-[#D4AF37]/60 bg-[#D4AF37]/5" : "border-white/5 hover:border-white/20"}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm text-white truncate flex-1">{e.title}</span>
                    <span className="font-mono text-[9px] tracking-widest" style={{ color: STATUS_COLOR[e.status] }}>{(e.status || "").toUpperCase()}</span>
                  </div>
                  <div className="text-[11px] text-slate-500 mt-0.5 truncate">
                    {e.protocol_name || "—"} · {e.target_stakeholder_name || "no target"}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Detail / Creator */}
        <div className="lg:col-span-2 space-y-5">
          {creating ? (
            <div className="nexus-glass-panel p-5" data-testid="create-engagement-form">
              <div className="flex items-center justify-between mb-3">
                <div className="label-eyebrow">// New engagement</div>
                <button onClick={() => setCreating(false)} className="text-slate-500 hover:text-white"><X className="w-3 h-3" /></button>
              </div>
              <div className="space-y-3">
                <input value={newEng.title} onChange={(e) => setNewEng({ ...newEng, title: e.target.value })} placeholder="Engagement title" data-testid="eng-title" className="w-full bg-[#0A111F] border border-white/10 text-white text-sm px-3 py-2 focus:border-[#D4AF37]/60 focus:outline-none" />
                <select value={newEng.protocol_type} onChange={(e) => setNewEng({ ...newEng, protocol_type: e.target.value })} data-testid="eng-protocol" className="w-full bg-[#0A111F] border border-white/10 text-white text-sm px-3 py-2 focus:border-[#D4AF37]/60 focus:outline-none">
                  {protocols.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
                <select value={newEng.target_stakeholder_id} onChange={(e) => setNewEng({ ...newEng, target_stakeholder_id: e.target.value })} data-testid="eng-target" className="w-full bg-[#0A111F] border border-white/10 text-white text-sm px-3 py-2 focus:border-[#D4AF37]/60 focus:outline-none">
                  <option value="">— select target stakeholder —</option>
                  {stakeholders.map((s) => <option key={s.id} value={s.id}>{s.name} · {s.organization}</option>)}
                </select>
                <input type="datetime-local" value={newEng.scheduled_date} onChange={(e) => setNewEng({ ...newEng, scheduled_date: e.target.value })} className="w-full bg-[#0A111F] border border-white/10 text-white text-sm px-3 py-2 focus:border-[#D4AF37]/60 focus:outline-none" />
                <textarea value={newEng.objective} onChange={(e) => setNewEng({ ...newEng, objective: e.target.value })} rows={2} placeholder="Strategic objective" className="w-full bg-[#0A111F] border border-white/10 text-white text-sm p-2.5 resize-none focus:border-[#D4AF37]/60 focus:outline-none" />
                <button onClick={save} data-testid="save-engagement" className="w-full py-2 text-[11px] font-mono tracking-widest" style={{ background: "var(--gold-500)", color: "#050B14" }}>SCHEDULE ENGAGEMENT</button>
              </div>
              {selectedProtocol && (
                <div className="mt-5 pt-4 border-t border-white/5">
                  <div className="label-eyebrow mb-2">// Protocol Rules</div>
                  <ul className="space-y-1">
                    {selectedProtocol.rules.map((r, i) => (
                      <li key={i} className="text-[12px] text-slate-300 leading-snug flex gap-2">
                        <ChevronRight className="w-3 h-3 mt-1 text-[#D4AF37] shrink-0" />
                        <span>{r}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ) : activeEng ? (
            <>
              <div className="nexus-glass-panel p-5" data-testid="engagement-detail">
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div>
                    <div className="label-eyebrow">{activeEng.protocol_name || "Engagement"}</div>
                    <h2 className="font-display text-xl text-white">{activeEng.title}</h2>
                    {activeEng.target_stakeholder_name && (
                      <div className="text-sm text-slate-400 mt-1">Target: {activeEng.target_stakeholder_name}</div>
                    )}
                  </div>
                  <button onClick={() => remove(activeEng.id)} className="text-slate-500 hover:text-red-400 text-[11px] font-mono tracking-widest border border-white/10 hover:border-red-500/40 px-2 py-1">DELETE</button>
                </div>
                {activeEng.objective && (
                  <div className="mb-3">
                    <div className="label-eyebrow">// Objective</div>
                    <p className="text-sm text-slate-200 mt-1">{activeEng.objective}</p>
                  </div>
                )}
                {activeEng.talking_points?.length > 0 && (
                  <div>
                    <div className="label-eyebrow mb-2">// Talking Points</div>
                    <ol className="space-y-1.5">
                      {activeEng.talking_points.map((p, i) => (
                        <li key={i} className="text-sm text-slate-200 flex gap-2 leading-snug">
                          <span className="font-mono text-xs text-[#D4AF37] mt-0.5">{String(i + 1).padStart(2, "0")}</span>
                          <span>{p}</span>
                        </li>
                      ))}
                    </ol>
                  </div>
                )}
                {activeEng.target_stakeholder_id && (
                  <button
                    onClick={() => findPath(activeEng.target_stakeholder_id)}
                    disabled={pathLoading}
                    data-testid="find-pathway-btn"
                    className="mt-4 text-[11px] font-mono tracking-widest px-3 py-2 border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/8 flex items-center gap-2 transition-colors"
                  >
                    {pathLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Users2 className="w-3 h-3" />}
                    MAP INFLUENCE PATHWAY
                  </button>
                )}
              </div>

              {influencePath && (
                <div className="nexus-glass-panel p-5" data-testid="influence-pathway">
                  <div className="label-eyebrow mb-2">// Influence pathway to {influencePath.target?.name}</div>
                  {influencePath.introducer_candidates.length === 0 ? (
                    <p className="text-sm text-slate-400">No introducer candidates in your current network. Build out adjacent relationships before approaching directly.</p>
                  ) : (
                    <div className="space-y-2">
                      {influencePath.introducer_candidates.map((c, i) => (
                        <div key={c.stakeholder_id} className="flex items-start gap-3 p-3 border border-white/5 hover:border-white/15 transition-all">
                          <Crown className="w-3.5 h-3.5 mt-0.5 shrink-0" style={{ color: i === 0 ? "var(--gold-400)" : "#94A3B8" }} />
                          <div className="flex-1 min-w-0">
                            <div className="text-sm text-white truncate">{c.name}</div>
                            <div className="text-[11px] text-slate-500 truncate">{c.organization} · {c.reasons.join(" · ")}</div>
                          </div>
                          <div className="font-display text-base" style={{ color: "var(--gold-400)" }}>{c.pathway_score}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          ) : (
            <div className="nexus-glass-panel p-8 text-center">
              <ScrollText className="w-8 h-8 mx-auto text-slate-600 mb-3" strokeWidth={1.5} />
              <h3 className="font-display text-lg text-white">Select or create an engagement</h3>
              <p className="text-sm text-slate-400 mt-1">Plan and orchestrate a high-level engagement with protocol-driven preparation.</p>
            </div>
          )}

          {/* Protocols catalog */}
          <div className="nexus-glass-panel p-5" data-testid="protocol-catalog">
            <div className="label-eyebrow mb-3">// Protocol Templates</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {protocols.map((p) => (
                <div key={p.id} className="p-3 border border-white/5 hover:border-white/15 transition-all">
                  <div className="text-sm text-white">{p.name}</div>
                  <div className="text-[11px] text-slate-500 mt-0.5 leading-snug">{p.tagline}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
